/**
 * State-level regulatory lookup for nuclear and energy development.
 * Deterministic fallback when Gemini is unavailable.
 *
 * Sources: NCSL State Nuclear Policy Database, NRC regulations,
 * DOE Office of Nuclear Energy state policy tracker (2024).
 */
import type { DataProvider, ProviderResult } from './types';
import { CACHE_TTL, gridCellKey } from './types';

export interface StatePolicy {
  nuclearStance: 'Favorable' | 'Neutral' | 'Restrictive';
  notes: string;
}

/**
 * How a site's state code was resolved from its coordinates, most
 * authoritative first:
 *  - `'tiger'`         — point-in-county against the local Census TIGER
 *                        counties snapshot (refdata);
 *  - `'fcc'`           — the live FCC Census Block API;
 *  - `'bbox-estimate'` — `estimateStateFromCoords`' coarse bounding boxes,
 *                        used only when both lookups failed. Overlaps at
 *                        state borders (Scranton, PA reads NY; a strip of
 *                        WV reads PA) and covers southern Ontario (Toronto
 *                        reads NY), so it is an estimate, never a fact.
 *
 * DAT-FOLLOWUP-4 (state-resolution provenance).
 */
export type StateResolutionTier = 'tiger' | 'fcc' | 'bbox-estimate';

/** A state code together with the tier that produced it. */
export interface ResolvedState {
  stateCode: string;
  resolvedBy: StateResolutionTier;
}

/** Resolved regulatory record for a site. Provider tier resolves
 *  stateCode only; nuclearStance and notes are filled by DAM or AI
 *  research tiers. */
export interface RegulatoryEvidenceData {
  stateCode: string | null;
  /** Populated only when legacy callers pass full stance data. */
  nuclearStance?: StatePolicy['nuclearStance'] | null;
  notes?: string | null;
  /**
   * DAT-FOLLOWUP-4 — how `stateCode` was resolved. Set by
   * `regulatoryDbProvider.fetch` whenever a state resolved; absent on
   * provider payloads cached before the field existed (the cache key is
   * versioned so those are not reused) and on legacy caller data. The SED
   * compile persists only the `'bbox-estimate'` tier (see
   * `RegulatoryEvidence.stateResolution`).
   */
  stateResolution?: StateResolutionTier;
}

// Nuclear permitting stance by state — based on state moratorium status,
// SMR legislation, and historical NRC licensing activity.
const STATE_POLICIES: Record<string, StatePolicy> = {
  // Favorable: active nuclear fleet, SMR legislation, or pro-nuclear policy
  AL: { nuclearStance: 'Favorable', notes: 'Active nuclear fleet (Browns Ferry, Farley)' },
  AZ: { nuclearStance: 'Favorable', notes: 'Palo Verde (largest US nuclear plant)' },
  AR: { nuclearStance: 'Favorable', notes: 'Arkansas Nuclear One' },
  GA: { nuclearStance: 'Favorable', notes: 'Vogtle 3&4 (newest US units)' },
  ID: { nuclearStance: 'Favorable', notes: 'INL, strong SMR support' },
  IL: { nuclearStance: 'Favorable', notes: 'Largest US nuclear fleet (11 units)' },
  LA: { nuclearStance: 'Favorable', notes: 'River Bend, Waterford' },
  MS: { nuclearStance: 'Favorable', notes: 'Grand Gulf' },
  MO: { nuclearStance: 'Favorable', notes: 'Callaway, pro-nuclear legislation' },
  NE: { nuclearStance: 'Favorable', notes: 'Cooper Station, SMR-friendly' },
  NC: { nuclearStance: 'Favorable', notes: 'Active nuclear fleet, Duke Energy' },
  OH: { nuclearStance: 'Favorable', notes: 'Davis-Besse, Perry' },
  PA: { nuclearStance: 'Favorable', notes: 'Limerick, Peach Bottom, Susquehanna' },
  SC: { nuclearStance: 'Favorable', notes: 'Oconee, Catawba, Summer; strong nuclear industry' },
  TN: { nuclearStance: 'Favorable', notes: 'Watts Bar, Sequoyah; TVA SMR planning' },
  TX: { nuclearStance: 'Favorable', notes: 'Comanche Peak, STP; ERCOT deregulated market, SMR support' },
  VA: { nuclearStance: 'Favorable', notes: 'North Anna, Surry; data center corridor' },
  WA: { nuclearStance: 'Favorable', notes: 'Columbia Generating; Hanford history, pro-SMR legislation' },
  WY: { nuclearStance: 'Favorable', notes: 'Natrium SMR project (TerraPower)' },

  // Neutral: no moratorium, but limited nuclear activity or mixed signals
  CO: { nuclearStance: 'Neutral', notes: 'No active nuclear; clean energy goals could support SMR' },
  FL: { nuclearStance: 'Neutral', notes: 'St. Lucie, Turkey Point; regulatory process well-understood' },
  IN: { nuclearStance: 'Neutral', notes: 'No active nuclear but no moratorium' },
  IA: { nuclearStance: 'Neutral', notes: 'Duane Arnold closed 2020; wind-heavy grid' },
  KS: { nuclearStance: 'Neutral', notes: 'Wolf Creek; moderate regulatory environment' },
  KY: { nuclearStance: 'Neutral', notes: 'No nuclear; coal transition could support SMR' },
  MD: { nuclearStance: 'Neutral', notes: 'Calvert Cliffs; environmental review requirements' },
  MI: { nuclearStance: 'Neutral', notes: 'Palisades potential restart; mixed policy' },
  NH: { nuclearStance: 'Neutral', notes: 'Seabrook; ISO-NE market' },
  NJ: { nuclearStance: 'Neutral', notes: 'Hope Creek, Salem; supportive but dense population' },
  NM: { nuclearStance: 'Neutral', notes: 'WIPP, Sandia Labs; DOE presence' },
  ND: { nuclearStance: 'Neutral', notes: 'No nuclear; energy-friendly state' },
  OK: { nuclearStance: 'Neutral', notes: 'No nuclear; energy-sector friendly' },
  SD: { nuclearStance: 'Neutral', notes: 'No nuclear; minimal regulatory framework' },
  UT: { nuclearStance: 'Neutral', notes: 'NuScale UAMPS project (paused); DOE support' },
  WI: { nuclearStance: 'Neutral', notes: 'Moratorium lifted 2016; Point Beach active' },
  WV: { nuclearStance: 'Neutral', notes: 'No nuclear; coal replacement opportunity' },

  // Restrictive: moratorium, anti-nuclear legislation, or very challenging permitting
  CA: { nuclearStance: 'Restrictive', notes: 'Diablo Canyon extended but no new builds; CEQA adds 2+ years' },
  CT: { nuclearStance: 'Restrictive', notes: 'Millstone active but moratorium on new builds' },
  HI: { nuclearStance: 'Restrictive', notes: 'No nuclear; island grid; 100% renewable target' },
  ME: { nuclearStance: 'Restrictive', notes: 'Maine Yankee closed; anti-nuclear sentiment' },
  MA: { nuclearStance: 'Restrictive', notes: 'Pilgrim closed 2019; restrictive permitting' },
  MN: { nuclearStance: 'Restrictive', notes: 'Nuclear moratorium in effect' },
  MT: { nuclearStance: 'Restrictive', notes: 'Ban on new nuclear construction' },
  NY: { nuclearStance: 'Restrictive', notes: 'Indian Point closed; strict environmental review' },
  OR: { nuclearStance: 'Restrictive', notes: 'Nuclear moratorium unless federal waste solution' },
  RI: { nuclearStance: 'Restrictive', notes: 'No nuclear; small state, limited grid capacity' },
  VT: { nuclearStance: 'Restrictive', notes: 'Vermont Yankee closed; anti-nuclear legislation' },

  // Default for unlisted states/territories
  AK: { nuclearStance: 'Neutral', notes: 'Remote grid; microreactor opportunities' },
  DC: { nuclearStance: 'Restrictive', notes: 'Urban; no generation siting' },
  DE: { nuclearStance: 'Neutral', notes: 'No nuclear; small state' },
  NV: { nuclearStance: 'Neutral', notes: 'Yucca Mountain history; no active nuclear' },
};

/**
 * Look up nuclear regulatory stance for a US state.
 * Returns the state policy if found, or a neutral default.
 */
export function getStatePolicy(stateCode: string): StatePolicy {
  const code = stateCode.toUpperCase().trim();
  return STATE_POLICIES[code] || { nuclearStance: 'Neutral', notes: 'State policy not yet catalogued' };
}

/** True when `stateCode` is a real entry in the curated dictionary
 *  (i.e. `getStatePolicy` returns a catalogued stance rather than the
 *  Neutral "not yet catalogued" default). Used by the library-usage
 *  auditor to set `regulatoryDb.entryFound` precisely. Accepts only
 *  the 2-letter code form (matching the dictionary keys). */
export function hasStatePolicy(stateCode: string | null | undefined): boolean {
  if (!stateCode) return false;
  return Object.prototype.hasOwnProperty.call(STATE_POLICIES, stateCode.toUpperCase().trim());
}

/** Standard fallback note when a site has no catalogued state-level
 *  regulatory data (non-US, or a coordinate that didn't resolve to a
 *  US state). Shared by `assembleReportContext` so the LLM doesn't
 *  invent a stance. */
export const NON_US_REGULATORY_FALLBACK_NOTE =
  'No state-level regulatory data available for this jurisdiction. Cite source: AI site-research or user-uploaded regulatory documents.';

/**
 * Render the regulatory-stance prompt block (Existing Library Ingest &
 * Usage Audit Prompts 5 & 8). When a state code + stance is supplied,
 * emits `## STATE REGULATORY STANCE` with the curated stance text;
 * otherwise emits the same heading with the non-US fallback note so the
 * model has explicit guidance instead of a void. Returns `{ text, used }`
 * where `used` is true only when a real stance was rendered (drives the
 * auditor's `stanceUsedInPrompt`).
 */
export function formatRegulatoryStanceContext(
  stateCode: string | null | undefined,
  stance: { nuclearStance: string; notes: string } | null | undefined,
): { text: string; used: boolean } {
  if (stateCode && stateCode.trim().length > 0 && stance) {
    return {
      text: [
        '## STATE REGULATORY STANCE',
        `State ${stateCode.trim()}: nuclear permitting stance is ${stance.nuclearStance}. ${stance.notes}`,
        'Reflect this stance explicitly in the regulatory / permitting sections of the report.',
      ].join('\n'),
      used: true,
    };
  }
  return { text: `## STATE REGULATORY STANCE\n${NON_US_REGULATORY_FALLBACK_NOTE}`, used: false };
}

/**
 * Resolve the state code from coordinates and record which tier answered.
 * Tiers, most authoritative first (RD-1 / C5): the local Census TIGER
 * counties snapshot, the live FCC Census Block API, then the coarse
 * bounding boxes of `estimateStateFromCoords` as the last resort. Returns
 * null when no tier resolves a state (e.g. outside the rough US box).
 */
export async function resolveStateWithProvenance(
  lat: number,
  lng: number,
  signal?: AbortSignal,
): Promise<ResolvedState | null> {
  try {
    const { resolveFipsFromRefdata } = await import('../refdata/fipsLookup');
    const local = await resolveFipsFromRefdata(lat, lng);
    if (local) return { stateCode: local.stateCode, resolvedBy: 'tiger' };
  } catch { /* fall through to FCC */ }
  try {
    const { fetchWithTimeout } = await import('./types');
    const res = await fetchWithTimeout(
      `https://geo.fcc.gov/api/census/block/find?format=json&latitude=${lat}&longitude=${lng}`,
      15_000,
      signal,
    );
    if (res.ok) {
      const json = await res.json();
      const code = json.State?.code;
      if (code) return { stateCode: code, resolvedBy: 'fcc' };
    }
  } catch { /* fall through to bounding box */ }
  const estimate = estimateStateFromCoords(lat, lng);
  return estimate ? { stateCode: estimate, resolvedBy: 'bbox-estimate' } : null;
}

/**
 * Resolve state code from coordinates (TIGER snapshot → FCC → bounding-box
 * estimate). Callers that must tell an estimate from a lookup use
 * `resolveStateWithProvenance`.
 */
export async function resolveStateFromCoords(
  lat: number,
  lng: number,
  signal?: AbortSignal,
): Promise<string | null> {
  return (await resolveStateWithProvenance(lat, lng, signal))?.stateCode ?? null;
}

/**
 * Synchronous fallback: estimate state code from coordinates using bounding boxes.
 * Rough approximation — only covers major states, overlaps exist at borders.
 */
export function estimateStateFromCoords(lat: number, lng: number): string | null {
  if (lat < 24 || lat > 50 || lng < -125 || lng > -66) return null;

  if (lat >= 25.8 && lat <= 36.5 && lng >= -106.6 && lng <= -93.5) return 'TX';
  if (lat >= 30.2 && lat <= 35 && lng >= -90 && lng <= -81.7) {
    if (lng < -85) return 'MS';
    if (lng < -83) return 'AL';
    return 'GA';
  }
  if (lat >= 33 && lat <= 37 && lng >= -84 && lng <= -75.5) {
    if (lat < 35) return 'SC';
    return 'NC';
  }
  if (lat >= 36 && lat <= 39.5 && lng >= -84 && lng <= -75) return 'VA';
  if (lat >= 32 && lat <= 42 && lng >= -125 && lng <= -114) return 'CA';
  if (lat >= 42 && lat <= 49 && lng >= -125 && lng <= -116.9) return 'WA';
  if (lat >= 42 && lat <= 49 && lng >= -117 && lng <= -111) return 'ID';
  if (lat >= 40.5 && lat <= 45 && lng >= -80 && lng <= -71.8) return 'NY';
  if (lat >= 39.7 && lat <= 42.5 && lng >= -80.5 && lng <= -74.7) return 'PA';
  if (lat >= 25 && lat <= 31 && lng >= -87.6 && lng <= -80) return 'FL';
  if (lat >= 36 && lat <= 40 && lng >= -90 && lng <= -81.5) return 'KY';
  if (lat >= 35 && lat <= 36.7 && lng >= -90 && lng <= -81.6) return 'TN';
  if (lat >= 41 && lat <= 43 && lng >= -91.5 && lng <= -87) return 'IA';
  if (lat >= 37 && lat <= 43 && lng >= -91 && lng <= -87) return 'IL';
  if (lat >= 42.5 && lat <= 47 && lng >= -92.9 && lng <= -86.8) return 'WI';
  if (lat >= 43 && lat <= 49 && lng >= -104.1 && lng <= -96.5) return 'SD';
  if (lat >= 41 && lat <= 45 && lng >= -104.1 && lng <= -95.3) return 'NE';

  return null;
}

/**
 * Cache contract for `regulatory-db`. v2 (DAT-FOLLOWUP-4) adds
 * `stateResolution` to the payload; a v1 entry cached from a bounding-box
 * fallback carries no marker, so it must not be served as if resolved.
 */
const REGULATORY_DB_CACHE_CONTRACT_VERSION = 'v2-state-resolution';

/** Per-tier source text for the provider's reference row. */
function stateSourceText(resolved: ResolvedState): string {
  switch (resolved.resolvedBy) {
    case 'tiger':
      return `${resolved.stateCode} state resolved from Census TIGER county boundaries + curated state policy stance`;
    case 'fcc':
      return `${resolved.stateCode} state resolved via FCC Census Block API + curated state policy stance`;
    case 'bbox-estimate':
      return `${resolved.stateCode} state ESTIMATED from a coordinate bounding box (Census TIGER and FCC lookups unavailable; verify) + curated state policy stance`;
  }
}

/**
 * Synthetic provider — wraps the local state-stance dictionary so the
 * SED top-up dispatcher can refresh the `regulatory` section like any
 * other provider (Content-Source Fixes Prompt 5). No network call; the
 * dictionary lives in this module. State code is resolved from the
 * (lat, lng) via the same census + bounding-box fallback used
 * elsewhere, and the tier that answered is recorded (DAT-FOLLOWUP-4).
 */
export const regulatoryDbProvider: DataProvider<RegulatoryEvidenceData> = {
  id: 'regulatory-db',
  name: 'State Regulatory Stance (curated)',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,
  cacheKey: ({ lat, lng }) => `${REGULATORY_DB_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,

  async fetch(
    lat: number,
    lng: number,
    _apiKey?: string,
    geo?: import('./types').ProviderGeoContext,
  ): Promise<ProviderResult<RegulatoryEvidenceData>> {
    // CA3: `(lat, lng)` is the boundary centroid when a polygon is drawn
    // (`regulatory-db` is in CENTROID_QUERY_PROVIDER_IDS), else the geocoded
    // address. The curated state-stance dictionary is keyed off the state
    // resolved from the query point — a mailbox is essentially always in the
    // same state as its parcel, so this is a coordinate-consistency change;
    // near a state line it's the parcel that matters.
    const now = new Date().toISOString();
    const resolved = await resolveStateWithProvenance(lat, lng, geo?.signal);
    if (!resolved) {
      return {
        data: { stateCode: null },
        sources: [{
          field: 'regulatory',
          source: 'FCC Census Block API — coordinates outside US coverage; state unresolved',
          retrievedAt: now,
        }],
      };
    }
    const { stateCode } = resolved;
    const policy = getStatePolicy(stateCode);
    return {
      data: {
        stateCode,
        nuclearStance: policy.nuclearStance,
        notes: policy.notes,
        stateResolution: resolved.resolvedBy,
      },
      sources: [{
        field: 'regulatory',
        source: stateSourceText(resolved),
        retrievedAt: now,
      }],
      // A bounding-box estimate is a degraded fallback (both lookups failed,
      // or the point is outside Census coverage): never persist it, so the
      // next build retries TIGER and FCC instead of serving the estimate for
      // the whole cache TTL.
      ...(resolved.resolvedBy === 'bbox-estimate' ? { cacheTtlMs: 0 } : {}),
    };
  },
};
